/**
 * Dominant power state for headline box per spec: one operationally important state.
 * Class priority: 1 (Generator, Grid Import) → 2 (Grid Export) → 3 (Battery Discharging, Battery Charging).
 * Grid has highest priority after generator: any material grid import/export (≥ 0.2 kW) is shown over battery.
 * Materiality: Gen always; Grid import/export ≥ 0.2 kW; Battery charge/discharge ≥ 0.2 kW.
 * Within same class, highest kW wins.
 * Labels and explanations are installer-native; explanation is for future alerts/explainability (not displayed yet).
 */

import { useMemo } from 'react';

/** Grid import/export threshold (kW). Any import/export ≥ this is shown as dominant over battery. */
export const GRID_THRESHOLD_KW = 0.2;
export const BATTERY_DOMINANT_THRESHOLD_KW = 0.2;

/** Display labels: installer-native; direction in label, never in sign. */
export type DominantPowerStateResult =
  | { label: 'Generator Running'; kw: number; explanation: string; subLabel?: string }
  | { label: 'Importing from Grid'; kw: number; explanation: string; subLabel?: string }
  | { label: 'Exporting to Grid'; kw: number; explanation: string; subLabel?: string }
  | { label: 'Running on Battery'; kw: number; explanation: string; subLabel?: string }
  | { label: 'Charging Battery'; kw: number; explanation: string; subLabel?: string }
  | { label: 'Curtailing'; kw: number; explanation: string; subLabel?: string }
  | { label: 'Self-Supplied'; explanation: string; subLabel?: string };

export interface UseDominantPowerStateParams {
  /** Generator power in kW (null if offline) */
  generatorKw: number | null;
  /** Grid power in kW (positive = export, negative = import); null if disconnected */
  gridKw: number | null;
  /** Battery power in kW (positive = charging, negative = discharging); magnitude used when override set */
  batteryKw: number;
  /** When set (e.g. from API flow flags), use this for battery candidate instead of sign of batteryKw */
  batteryStateOverride?: 'Charging' | 'Discharging' | 'Idle';
  /** Solar PV power in kW. When PV is active and outweighs battery contribution, shows Self-Supplied. */
  pvKw?: number;
  /** Battery state of charge (0-100). At ≥95%, battery flows are float/trickle — not operationally meaningful. */
  socPct?: number;
  /** Curtailed (wasted) power in kW. When active during Self-Supplied, shown as subLabel. */
  curtailmentKw?: number;
}

interface Candidate {
  type: 'generator' | 'grid-import' | 'grid-export' | 'battery-discharging' | 'battery-charging';
  class: 1 | 2 | 3;
  kw: number;
}

const EXPLANATIONS: Record<Candidate['type'] | 'balanced', string> = {
  generator: 'Generator is supplying the system.',
  'grid-import': 'The system is drawing power from the grid.',
  'grid-export': 'Solar generation exceeds on-site load.',
  'battery-discharging': 'Battery is covering current load.',
  'battery-charging': 'On-site generation is charging the battery.',
  balanced: 'On-site generation is meeting demand.',
};

function getCandidates(params: UseDominantPowerStateParams): Candidate[] {
  const { generatorKw, gridKw, batteryKw, batteryStateOverride } = params;
  const candidates: Candidate[] = [];

  if (generatorKw != null && generatorKw > 0) {
    candidates.push({ type: 'generator', class: 1, kw: generatorKw });
  }
  if (gridKw != null && gridKw <= -GRID_THRESHOLD_KW) {
    candidates.push({ type: 'grid-import', class: 1, kw: Math.abs(gridKw) });
  }
  if (gridKw != null && gridKw >= GRID_THRESHOLD_KW) {
    candidates.push({ type: 'grid-export', class: 2, kw: gridKw });
  }
  const battMag = Math.abs(batteryKw);
  if (battMag >= BATTERY_DOMINANT_THRESHOLD_KW) {
    const asCharging = batteryStateOverride === 'Charging' || (batteryStateOverride == null && batteryKw >= BATTERY_DOMINANT_THRESHOLD_KW);
    const asDischarging = batteryStateOverride === 'Discharging' || (batteryStateOverride == null && batteryKw <= -BATTERY_DOMINANT_THRESHOLD_KW);
    if (asDischarging) {
      candidates.push({ type: 'battery-discharging', class: 3, kw: battMag });
    }
    if (asCharging) {
      candidates.push({ type: 'battery-charging', class: 3, kw: battMag });
    }
  }
  // When override says Charging/Discharging but raw magnitude is below threshold, still add candidate so we show the state (use minimum kW for display).
  else if (batteryStateOverride === 'Charging' || batteryStateOverride === 'Discharging') {
    const kw = BATTERY_DOMINANT_THRESHOLD_KW;
    if (batteryStateOverride === 'Discharging') {
      candidates.push({ type: 'battery-discharging', class: 3, kw });
    }
    if (batteryStateOverride === 'Charging') {
      candidates.push({ type: 'battery-charging', class: 3, kw });
    }
  }

  return candidates;
}

/** SoC threshold above which battery flows are float/trickle and not operationally meaningful. */
const FLOAT_SOC_THRESHOLD = 95;

function pickDominant(candidates: Candidate[], pvKw?: number, socPct?: number): DominantPowerStateResult {
  if (candidates.length === 0) {
    return { label: 'Self-Supplied', explanation: EXPLANATIONS.balanced };
  }

  const pvActive = pvKw != null && pvKw > GRID_THRESHOLD_KW;
  // When battery is nearly full and PV is active, any battery flow is just float/trickle —
  // the inverter is throttling solar to match load. The system is self-supplied (and curtailing).
  const isFloating = pvActive && socPct != null && socPct >= FLOAT_SOC_THRESHOLD;

  const byClass = candidates.reduce((acc, c) => {
    if (!acc[c.class]) acc[c.class] = [];
    acc[c.class].push(c);
    return acc;
  }, {} as Record<1 | 2 | 3, Candidate[]>);

  for (const cls of [1, 2, 3] as const) {
    const list = byClass[cls];
    if (!list?.length) continue;
    const best = list.reduce((a, b) => (a.kw >= b.kw ? a : b));
    // Suppress battery states when PV is active and battery is full (float charging)
    // or when PV outweighs a small battery discharge (solar bridging minor fluctuations).
    if (best.type === 'battery-charging' && isFloating) {
      return { label: 'Self-Supplied', explanation: EXPLANATIONS.balanced };
    }
    if (best.type === 'battery-discharging' && pvActive && best.kw < pvKw!) {
      return { label: 'Self-Supplied', explanation: EXPLANATIONS.balanced };
    }
    switch (best.type) {
      case 'generator':
        return { label: 'Generator Running', kw: best.kw, explanation: EXPLANATIONS.generator };
      case 'grid-import':
        return { label: 'Importing from Grid', kw: best.kw, explanation: EXPLANATIONS['grid-import'] };
      case 'grid-export':
        return { label: 'Exporting to Grid', kw: best.kw, explanation: EXPLANATIONS['grid-export'] };
      case 'battery-discharging':
        return { label: 'Running on Battery', kw: best.kw, explanation: EXPLANATIONS['battery-discharging'] };
      case 'battery-charging':
        return { label: 'Charging Battery', kw: best.kw, explanation: EXPLANATIONS['battery-charging'] };
    }
  }

  return { label: 'Self-Supplied', explanation: EXPLANATIONS.balanced };
}

/** Threshold (kW) above which curtailment becomes primary dominant state instead of subLabel. */
const CURTAILMENT_PRIMARY_THRESHOLD_KW = 1.0;

/**
 * Pure function: returns the single dominant power state (for tests and hook).
 * Selection logic and class ordering unchanged; labels and explanation are installer-native.
 */
export function getDominantPowerState(params: UseDominantPowerStateParams): DominantPowerStateResult {
  const candidates = getCandidates(params);
  const result = pickDominant(candidates, params.pvKw, params.socPct);
  // Promote curtailment to primary state when actively curtailing ≥1 kW and would otherwise show Self-Supplied.
  if (result.label === 'Self-Supplied' && params.curtailmentKw != null && params.curtailmentKw >= CURTAILMENT_PRIMARY_THRESHOLD_KW) {
    return { label: 'Curtailing', kw: params.curtailmentKw, explanation: 'Battery is full — solar production is being limited.' };
  }
  // Attach curtailment subLabel for minor curtailment (< 1 kW).
  if (result.label === 'Self-Supplied' && params.curtailmentKw != null && params.curtailmentKw >= 0.1) {
    return { ...result, subLabel: `Curtailing ~${params.curtailmentKw.toFixed(1)} kW` };
  }
  return result;
}

/**
 * Returns the single dominant power state for the headline box.
 * One state only; no net values; generator always wins; grid import/export beats small battery flows.
 */
export function useDominantPowerState(params: UseDominantPowerStateParams): DominantPowerStateResult {
  const { generatorKw, gridKw, batteryKw, batteryStateOverride, pvKw, socPct, curtailmentKw } = params;
  return useMemo(
    () => getDominantPowerState({ generatorKw, gridKw, batteryKw, batteryStateOverride, pvKw, socPct, curtailmentKw }),
    [generatorKw, gridKw, batteryKw, batteryStateOverride, pvKw, socPct, curtailmentKw]
  );
}
